import { AlertTriangle, ScanSearch } from "lucide-react";
import type { ReactNode } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { RAG, TrendChart } from "@busright/ui";

import { ChartCard } from "@/components/ChartCard";
import { DevFacts } from "@/components/DevFacts";
import { DevRiskSubscores } from "@/components/DevRiskSubscores";
import { DevSerialInput } from "@/components/DevSerialInput";
import { DevTelemetryPanel } from "@/components/DevTelemetryPanel";
import { EmptyState } from "@/components/EmptyState";
import { PageHeader } from "@/components/PageHeader";
import { RiskTag } from "@/components/RiskTag";
import { useDevice } from "@/hooks/queries";
import { formatScore } from "@/lib/format";

// Device 360 — serial-bridged single-device drilldown. Normally reached via global search / links;
// the serial box at top makes it directly testable. Renders only fields the /api/device contract
// carries — identity facts, risk subscores, hardware/telemetry readings, and the subscore history.

// Real serials pulled from the telemetry outliers payload — known to resolve against /api/device.
const EXAMPLE_SERIALS: readonly string[] = ["R5GL31T31YE", "R52Y90K0THB", "R52Y5055J4H"] as const;

export const DevicePage = (): ReactNode => {
  const { serial } = useParams<{ serial: string }>();
  const navigate = useNavigate();
  const goToSerial = (next: string): void => {
    navigate(`/device/${encodeURIComponent(next)}`);
  };

  const { data, isLoading, isError, error } = useDevice(serial ?? "");
  const meta = data?.meta;
  const device = data?.data;

  const serialBox = (
    <ChartCard title="Look up a device" subtitle="Enter a serial or pick an example to drill in">
      <DevSerialInput onSubmit={goToSerial} examples={EXAMPLE_SERIALS} current={serial} />
    </ChartCard>
  );

  // No serial in the route → prompt for one rather than firing a query.
  if (!serial) {
    return (
      <>
        <PageHeader title="Device 360" description="Single-device drilldown — search or link in." />
        {serialBox}
        <div style={{ marginTop: 16 }}>
          <EmptyState
            title="No device selected"
            message="Enter a serial above to load its 360 view."
            icon={ScanSearch}
          />
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader title="Device 360" description={`Serial ${serial}`} meta={meta} />
      {serialBox}

      {isError ? (
        <div style={{ marginTop: 16 }}>
          <ChartCard title="Device">
            <EmptyState
              title="Couldn't load device"
              message={error instanceof Error ? error.message : `No device found for ${serial}`}
              icon={AlertTriangle}
            />
          </ChartCard>
        </div>
      ) : (
        <>
          <div style={{ marginTop: 16 }}>
            <ChartCard
              title="Identity"
              subtitle="Hardware, OS, and security facts"
              source="rpt_hw_device_latest"
              loading={isLoading}
              action={device?.risk ? <RiskTag tier={device.risk.riskTier} /> : undefined}
            >
              {device ? <DevFacts device={device} /> : <EmptyState title="No device facts" />}
            </ChartCard>
          </div>

          <div className="grid cols-3" style={{ marginTop: 16 }}>
            <ChartCard
              title="Risk subscores"
              subtitle={
                device?.risk
                  ? `Composite ${formatScore(device.risk.compositeHealthScore)} · vs WARN thresholds`
                  : "Latest per-subscore health"
              }
              source="rpt_hw_device_latest"
              loading={isLoading}
              className="span-2"
            >
              {device?.risk ? (
                <DevRiskSubscores risk={device.risk} />
              ) : (
                <EmptyState title="No risk row" message="This device has no scored risk row yet." />
              )}
            </ChartCard>

            <ChartCard
              title="Hardware & telemetry"
              subtitle="RAM, storage, last-known location"
              source="rpt_hw_device_latest"
              loading={isLoading}
            >
              {device ? (
                <DevTelemetryPanel device={device} dataAsOf={meta?.dataAsOf ?? null} />
              ) : (
                <EmptyState title="No telemetry" />
              )}
            </ChartCard>
          </div>

          <div style={{ marginTop: 16 }}>
            <ChartCard
              title="Subscore history"
              subtitle="This device's daily composite over the ~30-day health window"
              source="device_daily_hw_rollup"
              loading={isLoading}
            >
              {device && device.subscoreHistory.length > 0 ? (
                <TrendChart data={device.subscoreHistory} height={200} color={RAG.WARN} />
              ) : (
                <EmptyState
                  title="No history"
                  message="No daily composite history is available for this device."
                />
              )}
            </ChartCard>
          </div>
        </>
      )}
    </>
  );
};
